import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { IoGlobeOutline, IoChevronDown } from "react-icons/io5";
import { adminColors, radius } from "../../theme/adminTheme";

const faviconUrl = (domain) => `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;

// Claude-style web-search step card: globe icon + the real query that was
// sent, an expandable list of the REAL individual sources cited by the
// model (not a fake/fixed count), shown persistently for the lifetime of
// the message (survives after streaming finishes and after a page refresh
// via /history) rather than being a one-shot status line that disappears.
// `pending` distinguishes "query known, search still in flight" (no
// results yet) from a completed search that genuinely found zero results.
const WebSearchStep = ({ query, results = [], pending = false }) => {
    const [expanded, setExpanded] = useState(false);
    const count = results.length;

    return (
        <Box
            sx={{
                mt: 0.75,
                mb: 1,
                border: "1px solid #e5e7eb",
                borderRadius: radius.surface,
                overflow: "hidden",
                backgroundColor: adminColors.surface,
                maxWidth: 420
            }}
        >
            <Box
                onClick={() => count > 0 && setExpanded((v) => !v)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    cursor: count > 0 ? "pointer" : "default",
                    userSelect: "none"
                }}
            >
                <IoGlobeOutline
                    size={15}
                    color={adminColors.inkSoft}
                    style={{ flexShrink: 0, ...(pending && { animation: "webSearchPulse 1.4s ease-in-out infinite" }) }}
                />
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: "13px",
                        color: adminColors.ink,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        "@keyframes webSearchPulse": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.35 }
                        },
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" }
                    }}
                >
                    {query}
                </Typography>
                {pending ? (
                    <Typography variant="caption" sx={{ fontSize: "11.5px", color: adminColors.inkSoft, flexShrink: 0, fontStyle: "italic" }}>
                        Searching…
                    </Typography>
                ) : count > 0 && (
                    <>
                        <Typography variant="caption" sx={{ fontSize: "11.5px", color: adminColors.inkSoft, flexShrink: 0 }}>
                            {count} result{count === 1 ? "" : "s"}
                        </Typography>
                        <IoChevronDown
                            size={12}
                            color={adminColors.inkSoft}
                            style={{
                                flexShrink: 0,
                                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s ease"
                            }}
                        />
                    </>
                )}
            </Box>

            {!pending && count > 0 && (
                <Collapse in={expanded}>
                    <Box sx={{ px: 1.5, pb: 1, pt: 0.25, borderTop: "1px solid #e5e7eb" }}>
                        {results.map((r, i) => (
                            <Box
                                component="a"
                                key={r.url || i}
                                href={r.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    py: 0.6,
                                    textDecoration: "none",
                                    color: "inherit",
                                    "&:hover": { color: adminColors.accent }
                                }}
                            >
                                <Box
                                    component="img"
                                    src={faviconUrl(r.domain)}
                                    alt=""
                                    sx={{ width: 14, height: 14, borderRadius: "3px", flexShrink: 0 }}
                                    onError={(e) => { e.target.style.visibility = "hidden"; }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ fontSize: "12.5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >
                                    {r.title}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: "11px", color: adminColors.inkSoft, flexShrink: 0 }}>
                                    {r.domain}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Collapse>
            )}
        </Box>
    );
};

export default WebSearchStep;

